// scoutQuickEntry.js — v1.6.88 (B17)
// Shared helpers for Scout™ quick-entry surfaces (dashboard card, floating launcher, future feeders).
// Writes go directly to Supabase (scout_accounts). Backfills legacy localStorage
// data on first successful write per session so nothing captured before B17 is lost.
//
// Naming: Scout™ is the trademark family. Each entry carries a `source` tag so we
// can measure feeder performance: field | linkedin | rig | apollo | meeting | referral

import { supabase } from './supabase';
import { aliasCompanyFor } from './companySearch';

const STORAGE_KEY = 'bark-scout-data-v1';
const BACKFILL_FLAG = 'scout-backfill-completed-v1';

export const SCOUT_SOURCES = {
  FIELD:    'field',     // manual Cmd+K / dashboard card
  LINKEDIN: 'linkedin',  // Chrome extension (formerly "Hunt")
  RIG:      'rig',       // Rigs table bulk-scout
  APOLLO:   'apollo',    // Apollo bulk import
  MEETING:  'meeting',   // post-meeting drop-in (formerly Bark)
  REFERRAL: 'referral',  // manual referral
};

export const SCOUT_STAGES = [
  { value: 'active', label: 'Active', color: '#16a34a' },
  { value: 'target', label: 'Target', color: '#ea580c' },
  { value: 'cold',   label: 'Cold',   color: '#2563eb' },
];

/**
 * Add a Scout™ account directly to the database.
 * Returns { data, error } — mirrors Supabase's shape.
 */
export async function addScoutAccount({
  name,
  stage = 'target',
  notes = '',
  source = SCOUT_SOURCES.FIELD,
  partnerId = null,
  ownerLabel = 'Shane',
  createdBy = null,
}) {
  const trimmedName = (name || '').trim();
  if (!trimmedName) return { data: null, error: new Error('Account name required.') };

  const payload = {
    name: trimmedName,
    stage,
    notes: (notes || '').trim(),
    source,
    partner_id: partnerId,
    owner: ownerLabel,
    created_by: createdBy,
  };

  const { data, error } = await supabase
    .from('scout_accounts')
    .insert(payload)
    .select()
    .single();

  return { data, error };
}

/**
 * Count Scout™ accounts (optionally filtered by partner).
 * Used by dashboard card to show "N tracked" pill.
 */
export async function countScoutAccounts({ partnerId = null } = {}) {
  let query = supabase.from('scout_accounts').select('id', { count: 'exact', head: true });
  // NOTE: Ranch Rule — everyone sees everything, no owner scoping.
  // Partner filter is optional UX narrowing only (dashboard card scopes to active persona).
  if (partnerId) query = query.eq('partner_id', partnerId);
  const { count, error } = await query;
  return { count: count || 0, error };
}

/**
 * Cross-checker (v1.6.90 · B28) — check if a company with a similar name already
 * exists in the CRM. Returns up to 5 matches, ranked. Simple normalized-substring
 * match; deliberately not fuzzy so promoters see exactly what they'd merge into.
 *
 * Normalization: lowercase, strip common suffixes (inc, llc, ltd, co, corp),
 * collapse whitespace and punctuation. Reduces false-negatives from suffix drift.
 */
export async function findCompanyMatches(name) {
  const raw = (name || '').trim();
  if (!raw) return { matches: [], normalized: '' };

  const normalize = (s) => (s || '')
    .toLowerCase()
    .replace(/[.,'"\-_/]/g, ' ')
    .replace(/\b(inc|llc|ltd|co|corp|corporation|company|holdings|group|services|energy|resources)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const normalized = normalize(raw);
  if (!normalized) return { matches: [], normalized: '' };

  // Fetch a manageable slice (limit prevents pulling entire company table).
  // We match client-side because normalize logic isn't SQL-portable.
  const firstToken = normalized.split(' ')[0];
  if (!firstToken || firstToken.length < 2) return { matches: [], normalized };

  const { data, error } = await supabase
    .from('companies')
    .select('id, name')
    .is('merged_into_id', null)
    .is('deleted_at', null)
    .ilike('name', `%${firstToken}%`)
    .limit(50);

  if (error) return { matches: [], normalized, error };

  // #2793: a known alias (Occidental -> Oxy) is a perfect match even when no name contains the typed words
  const aliasCo = await aliasCompanyFor(supabase, raw);

  const scored = (data || [])
    .map((c) => {
      const cNorm = normalize(c.name);
      let score = 0;
      if (cNorm === normalized) score = 100;
      else if (cNorm.includes(normalized) || normalized.includes(cNorm)) score = 70;
      else if (cNorm.split(' ')[0] === firstToken) score = 40;
      return { ...c, score };
    })
    .filter((c) => c.score > 0)
    .sort((a, b) => b.score - a.score);
  if (aliasCo && !scored.some((c) => c.id === aliasCo.id)) scored.unshift({ ...aliasCo, score: 100 });

  return { matches: scored.slice(0, 5), normalized };
}

/**
 * One-time backfill: any accounts still sitting in localStorage from pre-B17
 * shipping get migrated to the database on first successful DB write. Runs at
 * most once per browser (guarded by BACKFILL_FLAG).
 *
 * Silent by design — this is cleanup, not user-facing.
 */
export async function backfillLegacyLocalStorage({ createdBy = null } = {}) {
  try {
    if (typeof window === 'undefined') return { migrated: 0, skipped: true };
    if (window.localStorage.getItem(BACKFILL_FLAG) === '1') return { migrated: 0, skipped: true };

    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      window.localStorage.setItem(BACKFILL_FLAG, '1');
      return { migrated: 0, skipped: true };
    }

    let parsed;
    try { parsed = JSON.parse(raw); } catch { parsed = null; }
    const accounts = (parsed && Array.isArray(parsed.accounts)) ? parsed.accounts : [];
    if (accounts.length === 0) {
      window.localStorage.setItem(BACKFILL_FLAG, '1');
      return { migrated: 0, skipped: true };
    }

    // Build insert rows. Preserve original createdAt where possible.
    const rows = accounts
      .filter((a) => a && (a.name || '').trim())
      .map((a) => ({
        name: (a.name || '').trim(),
        stage: ['active', 'target', 'cold'].includes(a.stage) ? a.stage : 'target',
        notes: (a.notes || '').trim(),
        source: SCOUT_SOURCES.FIELD, // legacy quick-entry always originated from field capture
        partner_id: a.partnerId || null,
        owner: a.owner || 'Shane',
        created_by: createdBy,
      }));

    if (rows.length === 0) {
      window.localStorage.setItem(BACKFILL_FLAG, '1');
      return { migrated: 0, skipped: true };
    }

    const { error } = await supabase.from('scout_accounts').insert(rows);
    if (error) {
      // Don't set the flag — retry on next session
      return { migrated: 0, error };
    }

    window.localStorage.setItem(BACKFILL_FLAG, '1');
    return { migrated: rows.length };
  } catch (e) {
    return { migrated: 0, error: e };
  }
}
